import { useEffect, useState } from "react";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import { ExtractedFieldData } from "../../models/extraction/ExtractedFieldData";
import { DefaultValueMatcher } from "../../models/matchers/DefaultValueMatcher";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { FuzzyFieldMatcher } from "../../models/matchers/FuzzyFieldMatcher";
import {
  KeyValueMatcher,
  KeyValueMatcherData,
} from "../../models/matchers/KeyValueMatcher";
import {
  convertAbsoluteToPercentage,
  PageSection,
} from "../../models/matchers/PageSection";
import { RegexFieldMatcher } from "../../models/matchers/RegexFieldMatcher";
import { SectionExtractor } from "../../services/extraction/SectionExtractor";
import { escapeRegExp } from "../../services/regex/escapeRegExp";
import { withSpanHighlights } from "../hoc/dlfEditing/withSpanHighlights";
import { DefaultValueDlfEditor } from "./defaultValue/DefaultValueDlfEditor";
import { FieldMatcherEditorProps } from "./FieldMatcherEditor";
import { FuzzyFieldMatcherEditor } from "./fuzzy/FuzzyFieldMatcherEditor";
import { HighlightableKeyValueDlfEditor } from "./keyValue/HighlightableKeyValueDlfEditor";
import { RegexFieldMatcherEditor } from "./regex/RegexFieldMatcherEditor";

export { AcceleratorDocFieldMatcherEditor };

type Props = {
  acceleratorDoc: AcceleratorDoc;
  extractedData: ExtractedFieldData;
  /**
   * Active Accelerator Page. This property can dictate the functionality of same <FieldMatcherEditor /> behaviour
   */
  pageIndex: number;
  /**
   * True if the MatcherEditor is "opened"". This will change which highlight(s) are sent to the ImageViewer.
   */
  open: boolean;
  /**
   * True if a different DlfEditor is open.
   */
  otherOpen: boolean;
} & FieldMatcherEditorProps<DlfMatcher>;

const AcceleratorDocFieldMatcherEditor = (props: Props) => {
  const [oldMatcherId, setOldMatcherId] = useState(
    props.matcher.getMatcherId(),
  );

  // update oldMatcherId when matcher prop changes
  useEffect(
    () => setOldMatcherId(props.matcher.getMatcherId()),
    [props.matcher],
  );

  const highlightKey: string = `${props.matcher.getMatcherId()}`;

  const update = (matcher: DlfMatcher) => props.update(matcher, oldMatcherId);

  const changedMatcherType = props.changedMatcherType
    ? (matcher: DlfMatcher) => props.changedMatcherType!(matcher, oldMatcherId)
    : undefined;

  switch (props.matcher.getDiscriminator()) {
    case "F_DEFAULT": {
      return (
        <DefaultValueDlfEditor
          key={props.matcher.getMatcherId()}
          update={update}
          docFieldConfig={props.docFieldConfig}
          labelColor={props.labelColor}
          labelBgColor={props.labelBgColor}
          changedMatcherType={changedMatcherType}
          matcher={props.matcher as DefaultValueMatcher}
        />
      );
    }
    case "F_REGEX": {
      return withSpanHighlights(
        RegexFieldMatcherEditor,
        props.matcher.getMatcherId(),
      )({
        ...props,
        highlightKey,
        matcher: props.matcher as RegexFieldMatcher,
        update,
        changedMatcherType,
      });
    }
    case "F_FUZZY": {
      return withSpanHighlights(
        FuzzyFieldMatcherEditor,
        props.matcher.getMatcherId(),
      )({
        ...props,
        highlightKey,
        matcher: props.matcher as FuzzyFieldMatcher,
        update,
        changedMatcherType,
      });
    }
    case "F_KEYVALUE": {
      return (
        <HighlightableKeyValueDlfEditor
          key={props.matcher.fieldName}
          {...props}
          highlightKey={highlightKey}
          matcher={props.matcher as KeyValueMatcher}
          changedMatcherType={changedMatcherType}
          update={update}
          populateKey={async () => {
            const spansUnderKey = await new SectionExtractor(
              props.pageIndex,
              (props.matcher as KeyValueMatcher).keyMatcher.section,
            ).extract(props.acceleratorDoc);
            if (!spansUnderKey) {
              return props.matcher as KeyValueMatcher;
            }
            const page = props.acceleratorDoc.getPage(props.pageIndex);
            const newKv = new KeyValueMatcher(
              props.matcher.toMatcherData() as KeyValueMatcherData,
            );
            newKv.keyMatcher.setNestedMatcherText(
              escapeRegExp(
                spansUnderKey.spans.map((x) => x.unicodeRemovedValue).join(" "),
              ),
            );
            // set coordinates on KeyMatcher
            if (newKv.keyMatcher.section.getSectionType() === "PAGE" && page) {
              console.debug("Set pageSection on KVM from SectionExtractor");
              console.log("%o", newKv.keyMatcher.section);
              const newPageSection = new PageSection(
                convertAbsoluteToPercentage(spansUnderKey.boundingBox, page),
              );
              if (newPageSection.isConfigured()) {
                newKv.keyMatcher.section = newPageSection;
                console.log(
                  "Set configured PageSection BB to %o",
                  newKv.keyMatcher.section,
                );
              } else {
                console.debug(
                  "Not setting PageSection %s that is not configured",
                  newPageSection,
                );
              }
            }
            return newKv;
          }}
        />
      );
    }
    default: {
      return null;
    }
  }
};
